import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import "moment/locale/id";
import { io } from "socket.io-client";
import { PublicEventAPI } from "../services/publicEvent.api";
import "./events-page.scss";

moment.locale("id");

/* ─── Socket ──────────────────────────────────────────────── */
const socket = io(
  process.env.REACT_APP_API_URL || "http://localhost:5003",
  { transports: ["websocket"] }
);

/* ─── Helpers ─────────────────────────────────────────────── */
function getStats(event) {
  const total =
    event.ticketTiers?.reduce((sum, t) => sum + Number(t.quota || 0), 0) || 0;
  const sold = typeof event.sold === "number" ? event.sold : 0;
  const remaining =
    typeof event.remaining === "number"
      ? event.remaining
      : Math.max(total - sold, 0);
  const percent =
    typeof event.percent === "number"
      ? event.percent
      : total > 0
      ? Math.round((sold / total) * 100)
      : 0;
  const minPrice =
    event.ticketTiers?.length > 0
      ? Math.min(...event.ticketTiers.map((t) => t.price))
      : 0;
  const isSoldOut = total > 0 && remaining === 0;
  return { remaining, minPrice, percent, isSoldOut, total };
}

function formatPrice(price) {
  if (price === 0) return "Gratis";
  return "Rp " + price.toLocaleString("id-ID");
}

/* ─── Skeleton Card ───────────────────────────────────────── */
function SkeletonCard() {
  return (
    <div className="ep-card ep-card--skeleton">
      <div className="ep-card__image ep-skeleton" />
      <div className="ep-card__body">
        <div className="ep-skeleton ep-skeleton--badge" />
        <div className="ep-skeleton ep-skeleton--title" />
        <div className="ep-skeleton ep-skeleton--meta" />
        <div className="ep-skeleton ep-skeleton--meta ep-skeleton--short" />
        <div className="ep-card__footer">
          <div className="ep-skeleton ep-skeleton--price" />
          <div className="ep-skeleton ep-skeleton--btn" />
        </div>
      </div>
    </div>
  );
}

/* ─── Empty State ─────────────────────────────────────────── */
function EmptyState({ filter }) {
  const messages = {
    all: { icon: "🗓️", title: "Belum ada event", sub: "Pantau terus untuk event & kegiatan GKY mendatang!" },
    upcoming: { icon: "🔭", title: "Tidak ada event upcoming", sub: "Belum ada event yang dijadwalkan. Cek lagi nanti ya!" },
    past: { icon: "📦", title: "Belum ada event selesai", sub: "Riwayat event akan muncul di sini." },
  };
  const msg = messages[filter] || messages.all;

  return (
    <div className="ep-empty">
      <div className="ep-empty__icon">{msg.icon}</div>
      <h3 className="ep-empty__title">{msg.title}</h3>
      <p className="ep-empty__sub">{msg.sub}</p>
      <Link to="/" className="ep-empty__link">
        ← Kembali ke Beranda
      </Link>
    </div>
  );
}

/* ─── Event Card ──────────────────────────────────────────── */
function EventCard({ event }) {
  const isUpcoming = moment(event.date).isSameOrAfter(moment(), "day");
  const stats = getStats(event);
  const img =
    event.headerImage?.url ||
    "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?q=80&w=800";

  let badgeClass = "ep-badge--past";
  let badgeLabel = "Selesai";
  if (stats.isSoldOut && isUpcoming) {
    badgeClass = "ep-badge--soldout";
    badgeLabel = "Sold Out";
  } else if (isUpcoming) {
    badgeClass = "ep-badge--upcoming";
    badgeLabel = "Upcoming";
  }

  return (
    <Link
      to={`/events/${event.slug}`}
      className={`ep-card ${!isUpcoming ? "ep-card--past" : ""}`}
    >
      <div className="ep-card__image-wrap">
        <img src={img} alt={event.title} className="ep-card__img" />

        {/* Date badge */}
        <div className="ep-card__date-badge">
          <span className="ep-card__date-day">
            {moment(event.date).format("D")}
          </span>
          <span className="ep-card__date-month">
            {moment(event.date).format("MMM")}
          </span>
        </div>

        {/* Status badge */}
        <span className={`ep-badge ${badgeClass}`}>{badgeLabel}</span>

        {!isUpcoming && <div className="ep-card__past-overlay" />}
      </div>

      <div className="ep-card__body">
        <h3 className="ep-card__title">{event.title}</h3>

        <div className="ep-card__meta">
          <span className="ep-card__meta-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {event.location?.name || "Lokasi TBA"}
          </span>
          <span className="ep-card__meta-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            {event.timeStart
              ? `${event.timeStart}${event.timeEnd ? " – " + event.timeEnd : ""}`
              : moment(event.date).format("D MMMM YYYY")}
          </span>
        </div>

        {isUpcoming && stats.isSoldOut && (
          <div className="ep-card__soldout-bar">Tiket Habis Terjual 🔥</div>
        )}

        <div className="ep-card__footer">
          <div className="ep-card__price">
            {formatPrice(stats.minPrice)}
          </div>
          <span className="ep-card__cta">Lihat Detail →</span>
        </div>
      </div>
    </Link>
  );
}

/* ─── Single Event Hero ───────────────────────────────────── */
function SingleEventHero({ event }) {
  const isUpcoming = moment(event.date).isSameOrAfter(moment(), "day");
  const stats = getStats(event);
  const img =
    event.headerImage?.url ||
    "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?q=80&w=1600";

  return (
    <div className="ep-single">
      <div className="ep-single__image-wrap">
        <img src={img} alt={event.title} className="ep-single__img" />
        {!isUpcoming && <div className="ep-single__past-overlay" />}
        <span className={`ep-badge ep-badge--lg ${isUpcoming ? "ep-badge--upcoming" : "ep-badge--past"}`}>
          {stats.isSoldOut && isUpcoming ? "Sold Out" : isUpcoming ? "Upcoming" : "Selesai"}
        </span>
      </div>

      <div className="ep-single__body">
        <div className="ep-single__date-tag">
          {moment(event.date).format("dddd, D MMMM YYYY")}
        </div>
        <h2 className="ep-single__title">{event.title}</h2>

        <div className="ep-single__details">
          <div className="ep-single__detail-item">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span>{event.location?.name || "Lokasi TBA"}</span>
          </div>
          {event.timeStart && (
            <div className="ep-single__detail-item">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>{event.timeStart}{event.timeEnd ? ` – ${event.timeEnd}` : ""} WIB</span>
            </div>
          )}
        </div>

        <div className="ep-single__footer">
          <div className="ep-single__price-group">
            <span className="ep-single__price-label">Mulai dari</span>
            <span className="ep-single__price">{formatPrice(stats.minPrice)}</span>
          </div>
          <Link to={`/events/${event.slug}`} className="ep-single__btn">
            {isUpcoming && !stats.isSoldOut ? "Daftar Sekarang" : "Lihat Detail"}
          </Link>
        </div>
      </div>
    </div>
  );
}

const PAGE_SIZE = 6;

/* ─── Main Page ───────────────────────────────────────────── */
export default function EventsPage() {
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);

  /* Fetch */
  const fetchEvents = async () => {
    try {
      const res = await PublicEventAPI.list();
      const data = res.data;
      if (!data?.length) { setAllEvents([]); setLoading(false); return; }

      const now = moment();
      const sorted = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
      const upcoming = sorted.filter((e) => moment(e.date).isSameOrAfter(now, "day"));
      const past = sorted.filter((e) => moment(e.date).isBefore(now, "day")).reverse();

      setAllEvents([...upcoming, ...past]);
    } catch (err) {
      console.error("Failed to fetch events:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(); }, []);

  /* Realtime */
  useEffect(() => {
    const handleUpdate = async ({ eventId }) => {
      if (!eventId) return;
      try {
        const res = await PublicEventAPI.getTicketStats(eventId);
        const stats = res.data.stats;
        const totalSold = stats.reduce((s, x) => s + x.sold, 0);
        const totalQuota = stats.reduce((s, x) => s + x.quota, 0);
        const patch = {
          sold: totalSold,
          remaining: Math.max(totalQuota - totalSold, 0),
          percent: totalQuota > 0 ? Math.round((totalSold / totalQuota) * 100) : 0,
        };
        setAllEvents((prev) =>
          prev.map((e) => (e._id === eventId ? { ...e, ...patch } : e))
        );
      } catch (err) {
        console.error("Realtime update error:", err);
      }
    };
    socket.on("event:ticket-updated", handleUpdate);
    return () => socket.off("event:ticket-updated", handleUpdate);
  }, []);

  /* Reset page when filter changes */
  useEffect(() => { setPage(1); }, [filter]);

  /* Filtered list */
  const filteredEvents = useMemo(() => {
    const now = moment();
    if (filter === "upcoming") return allEvents.filter((e) => moment(e.date).isSameOrAfter(now, "day"));
    if (filter === "past") return allEvents.filter((e) => moment(e.date).isBefore(now, "day"));
    return allEvents;
  }, [allEvents, filter]);

  /* Pagination */
  const totalPages = Math.ceil(filteredEvents.length / PAGE_SIZE);
  const pagedEvents = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredEvents.slice(start, start + PAGE_SIZE);
  }, [filteredEvents, page]);

  const countStart = filteredEvents.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const countEnd = Math.min(page * PAGE_SIZE, filteredEvents.length);

  /* Counts per tab */
  const upcomingCount = useMemo(
    () => allEvents.filter((e) => moment(e.date).isSameOrAfter(moment(), "day")).length,
    [allEvents]
  );
  const pastCount = useMemo(
    () => allEvents.filter((e) => moment(e.date).isBefore(moment(), "day")).length,
    [allEvents]
  );

  const tabCounts = { all: allEvents.length, upcoming: upcomingCount, past: pastCount };

  /* ─── Render ─── */
  return (
    <div className="ep-page">

      {/* ── Hero Header ── */}
      <div className="ep-hero">
        <div className="ep-hero__inner">
          {/* <div className="ep-hero__eyebrow">
            <span className="ep-hero__dot" />
            GKY Sinode
          </div> */}
          <h1 className="ep-hero__title">Event &amp; Kegiatan</h1>
          {/* <p className="ep-hero__sub">
            Temukan retreat, pembinaan iman, konser ibadah, dan kegiatan gereja
            lainnya — semuanya di satu tempat.
          </p> */}
          {/* {!loading && allEvents.length > 0 && (
            <div className="ep-hero__stats">
              <div className="ep-hero__stat">
                <span className="ep-hero__stat-num">{allEvents.length}</span>
                <span className="ep-hero__stat-lbl">Total Event</span>
              </div>
              <div className="ep-hero__stat-divider" />
              <div className="ep-hero__stat">
                <span className="ep-hero__stat-num">{upcomingCount}</span>
                <span className="ep-hero__stat-lbl">Upcoming</span>
              </div>
            </div>
          )} */}
        </div>
        <div className="ep-hero__wave">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
            <path d="M0,0 Q720,80 1440,0 L1440,80 L0,80 Z" fill="#F7F9FC" />
          </svg>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="ep-body">
        <div className="ep-container">

          {/* Filter bar */}
          {!loading && allEvents.length > 1 && (
            <div className="ep-filters">
              {["all", "upcoming", "past"].map((f) => (
                <button
                  key={f}
                  className={`ep-filter-btn ${filter === f ? "ep-filter-btn--active" : ""}`}
                  onClick={() => setFilter(f)}
                >
                  {f === "all" ? "Semua" : f === "upcoming" ? "Upcoming" : "Selesai"}
                  <span className="ep-filter-count">{tabCounts[f]}</span>
                </button>
              ))}
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="ep-grid">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          )}

          {/* Empty */}
          {!loading && filteredEvents.length === 0 && (
            <EmptyState filter={filter} />
          )}

          {/* Single event — full width hero */}
          {!loading && filteredEvents.length === 1 && (
            <SingleEventHero event={filteredEvents[0]} />
          )}

          {/* Grid — multiple filtered results */}
          {!loading && filteredEvents.length > 1 && (
            <>
              {/* Counter */}
              <div className="ep-counter">
                Menampilkan{" "}
                <strong>{countStart}–{countEnd}</strong>{" "}
                dari <strong>{filteredEvents.length}</strong> event
              </div>

              <div className="ep-grid">
                {pagedEvents.map((event) => (
                  <EventCard key={event._id} event={event} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="ep-pagination">
                  <button
                    className="ep-page-btn"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    ← Sebelumnya
                  </button>

                  <div className="ep-page-numbers">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        className={`ep-page-num ${p === page ? "ep-page-num--active" : ""}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    ))}
                  </div>

                  <button
                    className="ep-page-btn"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Berikutnya →
                  </button>
                </div>
              )}
            </>
          )}

        </div>
      </div>

    </div>
  );
}
